import React, { useState, useEffect, useRef } from 'react';
import kinesisService from '../services/kinesisService';
import './FeedbackForm.css';

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    overallImpression: 3,
    clarityOfPresentation: 3,
    demoRelevance: 3,
    engagement: '',
    mostValuablePart: '',
    freeFeedback: '',
    email: '',
    lang: 'en'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [metrics, setMetrics] = useState({
    inputStartTime: null,
    lastInputTime: null,
    inputDurationMs: 0,
    timeSinceLastInputMs: 0,
    totalSubmissionTimeMs: 0
  });

  const formRef = useRef(null);
  const startTimeRef = useRef(null);

  // Email validation function
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return true; // Empty email is valid (optional field)
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Initialize metrics tracking
  useEffect(() => {
    startTimeRef.current = Date.now();
    setMetrics(prev => ({
      ...prev,
      inputStartTime: startTimeRef.current,
      lastInputTime: startTimeRef.current
    }));
  }, []);

  // Update metrics on input
  const updateMetrics = () => {
    const now = Date.now();
    const inputDuration = now - startTimeRef.current;
    const timeSinceLastInput = metrics.lastInputTime ? now - metrics.lastInputTime : 0;
    
    setMetrics(prev => ({
      ...prev,
      inputDurationMs: inputDuration,
      timeSinceLastInputMs: timeSinceLastInput,
      lastInputTime: now
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateMetrics();
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate email if it's the email field
    if (name === 'email') {
      if (validateEmail(value)) {
        setEmailError('');
      } else {
        setEmailError('Please enter a valid email address or leave empty');
      }
    }
  };

  const handleRatingChange = (field, score) => {
    updateMetrics();
    setFormData(prev => ({
      ...prev,
      [field]: parseInt(score)
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email before submission
    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address or leave empty');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submissionStartTime = Date.now();
      const totalSubmissionTime = submissionStartTime - startTimeRef.current;

      // Update metrics with total submission time
      const updatedMetrics = {
        ...metrics,
        totalSubmissionTimeMs: totalSubmissionTime
      };

      // Log metrics to console (hidden from user)
      console.log('Form Metrics:', {
        inputDurationMs: metrics.inputDurationMs,
        timeSinceLastInputMs: metrics.timeSinceLastInputMs,
        totalSubmissionTimeMs: totalSubmissionTime,
        formData: formData
      });

      // Send data to Kinesis using the service
      const result = await kinesisService.sendFormData(formData, updatedMetrics);
      
      console.log('Successfully sent to Kinesis:', result);
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        overallImpression: 3,
        clarityOfPresentation: 3,
        demoRelevance: 3,
        engagement: '',
        mostValuablePart: '',
        freeFeedback: '',
        email: '',
        lang: 'en'
      });
      
      // Clear email error
      setEmailError('');
      
      // Reset metrics
      startTimeRef.current = Date.now();
      setMetrics({
        inputStartTime: startTimeRef.current,
        lastInputTime: startTimeRef.current,
        inputDurationMs: 0,
        timeSinceLastInputMs: 0,
        totalSubmissionTimeMs: 0
      });

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRatingInput = (field, label, description) => (
    <div className="rating-input">
      <label>{label}</label>
      {description && <p className="rating-description">{description}</p>}
      <div className="rating-buttons">
        {[1, 2, 3, 4, 5].map(score => (
          <button
            key={score}
            type="button"
            className={`rating-btn ${score <= formData[field] ? 'active' : ''}`}
            onClick={() => handleRatingChange(field, score)}
          >
            ⭐
          </button>
        ))}
      </div>
      <div className="rating-labels">
        <span>1 (Poor)</span>
        <span>5 (Excellent)</span>
      </div>
    </div>
  );

  const renderMCQInput = (field, label, options) => (
    <div className="mcq-input">
      <label>{label}</label>
      <div className="mcq-options">
        {options.map((option, index) => (
          <label key={index} className="mcq-option">
            <input
              type="radio"
              name={field}
              value={option.value}
              checked={formData[field] === option.value}
              onChange={handleInputChange}
            />
            <span className="option-emoji">{option.emoji}</span>
            <span className="option-text">{option.text}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="feedback-form-container">
      <form ref={formRef} onSubmit={handleSubmit} className="feedback-form">
        <h2>Presentation Feedback</h2>
        
        {/* 1. Overall Impression */}
        <div className="form-group">
          {renderRatingInput('overallImpression', '1. Overall Impression', '⭐ 1 (Poor) → ⭐⭐⭐⭐⭐ 5 (Excellent)')}
        </div>

        {/* 2. Clarity of Presentation */}
        <div className="form-group">
          {renderRatingInput('clarityOfPresentation', '2. Clarity of Presentation', 'How clear and understandable was the content?')}
        </div>

        {/* 3. Demo Relevance */}
        <div className="form-group">
          {renderRatingInput('demoRelevance', '3. Demo Relevance', 'How useful or relevant did you find the live demo?')}
        </div>

        {/* 4. Engagement */}
        <div className="form-group">
          {renderMCQInput('engagement', '4. Engagement - During the session, I felt…', [
            { emoji: '👎', value: 'bored', text: 'Bored / disengaged' },
            { emoji: '🙂', value: 'neutral', text: 'Neutral' },
            { emoji: '👍', value: 'engaged', text: 'Engaged and interested' },
            { emoji: '🤩', value: 'excited', text: 'Very excited / inspired' }
          ])}
        </div>

        {/* 5. Most Valuable Part */}
        <div className="form-group">
          {renderMCQInput('mostValuablePart', '5. Most Valuable Part - What did you find most valuable?', [
            { emoji: '☁️', value: 'aws_services', text: 'Explanation of AWS services' },
            { emoji: '📊', value: 'feedback_wall', text: 'Real-time feedback wall demo' },
            { emoji: '🛡️', value: 'fraud_detection', text: 'Fraud/inappropriate comment detection' },
            { emoji: '🤖', value: 'bedrock_summaries', text: 'Bedrock summaries and insights' }
          ])}
        </div>

        {/* 6. Free Feedback */}
        <div className="form-group">
          <label htmlFor="freeFeedback">6. Free Feedback</label>
          <p className="field-description">Any other comments you'd like to share?</p>
          <textarea
            id="freeFeedback"
            name="freeFeedback"
            value={formData.freeFeedback}
            onChange={handleInputChange}
            placeholder="Any other thoughts or feedback..."
            rows="3"
          />
        </div>

        {/* 7. Email (Optional) */}
        <div className="form-group">
          <label htmlFor="email">7. Email (Optional)</label>
          <p className="field-description">If you want to earn an award, please enter a valid email address.</p>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your.email@example.com"
            className={emailError ? 'error' : ''}
          />
          {emailError && <div className="error-message">{emailError}</div>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="submit-btn"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </button>

        {submitStatus === 'success' && (
          <div className="status-message success">
            ✅ Feedback submitted successfully! Thank you for your input.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="status-message error">
            ❌ Error submitting feedback. Please try again.
          </div>
        )}
      </form>
    </div>
  );
};

export default FeedbackForm;
